/**
 * Muhurta Engine - Core calculations for electional astrology
 * Calculates Tithi, Nakshatra favorability, Hora, and other Muhurta factors
 */

import * as Astronomy from 'astronomy-engine';

export type ActivityType =
  | 'business_start'
  | 'property_purchase'
  | 'graha_pravesh'
  | 'vehicle_purchase'
  | 'surgery'
  | 'ivf_planning'
  | 'cesarean';

export interface TithiInfo {
  number: number; // 1-30 (1-15 Shukla, 16-30 Krishna)
  name: string;
  paksha: 'shukla' | 'krishna'; // Waxing or waning
  percentage: number; // % completed
}

export interface MuhurtaFactors {
  tithi: TithiInfo;
  nakshatra: number; // 1-27
  nakshatraName: string;
  isWaxingMoon: boolean;
  moonPhase: number; // 0-1 (0=new, 0.5=full)
  dayOfWeek: string;
  hora: string; // Planetary hora
  isRahuKaal: boolean;
  isGandanta: boolean;
}

export interface NatalChartScoreInput {
  activity: ActivityType;
  houseStrengths?: Record<number, number>; // 1-12 houses with bhava bala
  planetaryPeriods?: {
    MD: string;
    AD: string;
    PD: string;
    SD: string;
  };
}

export interface TransitScoreInput {
  activity: ActivityType;
  transitPlanets?: Record<string, { house: number; sign: number }>;
  natalPlanets?: Record<string, { house: number; sign: number }>;
}

// Nakshatra names (1-27)
const NAKSHATRA_NAMES = [
  'Ashwini', 'Bharani', 'Krittika', 'Rohini', 'Mrigashira', 'Ardra',
  'Punarvasu', 'Pushya', 'Ashlesha', 'Magha', 'Purva Phalguni', 'Uttara Phalguni',
  'Hasta', 'Chitra', 'Swati', 'Vishakha', 'Anuradha', 'Jyeshtha',
  'Mula', 'Purva Ashadha', 'Uttara Ashadha', 'Shravana', 'Dhanishta', 'Shatabhisha',
  'Purva Bhadrapada', 'Uttara Bhadrapada', 'Revati'
];

// Tithi names (1-15 for both pakshas)
const TITHI_NAMES = [
  'Pratipada', 'Dwitiya', 'Tritiya', 'Chaturthi', 'Panchami',
  'Shashthi', 'Saptami', 'Ashtami', 'Navami', 'Dashami',
  'Ekadashi', 'Dwadashi', 'Trayodashi', 'Chaturdashi', 'Purnima/Amavasya'
];

/**
 * Calculate Tithi (lunar day) - based on Sun-Moon angular separation
 * Each tithi = 12° of separation
 */
export function getTithi(date: Date): TithiInfo {
  const time = Astronomy.MakeTime(date);
  
  // Get Sun and Moon geocentric vectors
  const sunVec = Astronomy.GeoVector(Astronomy.Body.Sun, time, true);
  const moonVec = Astronomy.GeoVector(Astronomy.Body.Moon, time, true);
  
  const sunEcl = Astronomy.Ecliptic(sunVec);
  const moonEcl = Astronomy.Ecliptic(moonVec);

  // Calculate angular separation (0-360°)
  let separation = moonEcl.elon - sunEcl.elon;
  if (separation < 0) separation += 360;

  // Each tithi spans 12° (360° / 30 tithis)
  const tithiFloat = separation / 12;
  const tithiNumber = Math.floor(tithiFloat) + 1; // 1-30
  const percentage = (tithiFloat % 1) * 100;

  // Determine paksha
  const paksha: 'shukla' | 'krishna' = tithiNumber <= 15 ? 'shukla' : 'krishna';
  
  // Get tithi name
  const nameIndex = paksha === 'shukla' ? tithiNumber - 1 : tithiNumber - 16;
  const name = TITHI_NAMES[nameIndex] || 'Unknown';

  return {
    number: tithiNumber,
    name,
    paksha,
    percentage: Math.round(percentage * 10) / 10,
  };
}

/**
 * Get nakshatra number and name from lunar longitude
 */
export function getNakshatraFromLongitude(moonLongitude: number): { number: number; name: string } {
  // Each nakshatra spans 13° 20' (360° / 27)
  const nakshatraNumber = Math.floor(moonLongitude / (360 / 27)) + 1;
  const name = NAKSHATRA_NAMES[nakshatraNumber - 1] || 'Unknown';
  
  return { number: nakshatraNumber, name };
}

/**
 * Calculate moon phase (0 = new moon, 0.5 = full moon, 1 = new moon again)
 */
export function getMoonPhase(date: Date): number {
  const time = Astronomy.MakeTime(date);
  
  // Get Sun and Moon geocentric vectors
  const sunVec = Astronomy.GeoVector(Astronomy.Body.Sun, time, true);
  const moonVec = Astronomy.GeoVector(Astronomy.Body.Moon, time, true);
  
  const sunEcl = Astronomy.Ecliptic(sunVec);
  const moonEcl = Astronomy.Ecliptic(moonVec);

  let separation = moonEcl.elon - sunEcl.elon;
  if (separation < 0) separation += 360;

  return separation / 360;
}

/**
 * Calculate Hora (planetary hour) - simplified day-based calculation
 * Traditional calculation requires sunrise time, this is a simplified approximation
 */
export function getHora(date: Date): string {
  const dayOfWeek = date.getDay(); // 0=Sunday, 1=Monday, etc.
  const hour = date.getHours();

  // Day lords: Sun, Moon, Mars, Mercury, Jupiter, Venus, Saturn
  const dayLords = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn'];
  const horaSequence = ['Sun', 'Venus', 'Mercury', 'Moon', 'Saturn', 'Jupiter', 'Mars'];

  // Start with day lord and cycle through hora sequence
  const dayLord = dayLords[dayOfWeek];
  const startIndex = horaSequence.indexOf(dayLord);
  const horaIndex = (startIndex + hour) % 7;

  return horaSequence[horaIndex];
}

/**
 * Check if nakshatra is favorable for a specific activity
 * Returns score: 1 (excellent), 0.5 (moderate), 0 (neutral), -0.5 (caution), -1 (avoid)
 */
export function getNakshatraScore(nakshatra: number, activity: ActivityType): number {
  // Favorable nakshatras by activity type
  const favorableNakshatras: Record<ActivityType, { excellent: number[]; good: number[]; avoid: number[] }> = {
    business_start: {
      excellent: [8, 14, 23, 27], // Pushya, Hasta, Shravana, Revati
      good: [4, 12, 13, 17], // Rohini, Uttara Phalguni, Hasta, Anuradha
      avoid: [6, 9, 16, 19], // Ardra, Ashlesha, Vishakha, Mula
    },
    property_purchase: {
      excellent: [4, 12, 13, 26], // Rohini, Uttara Phalguni, Hasta, Uttara Bhadrapada
      good: [7, 11, 21, 27], // Punarvasu, Purva Phalguni, Uttara Ashadha, Revati
      avoid: [6, 9, 16, 19, 25], // Ardra, Ashlesha, Vishakha, Mula, Purva Bhadrapada
    },
    graha_pravesh: {
      excellent: [4, 8, 12, 13, 27], // Rohini, Pushya, Uttara Phalguni, Hasta, Revati
      good: [3, 7, 11, 21, 26], // Krittika, Punarvasu, Purva Phalguni, Uttara Ashadha, Uttara Bhadrapada
      avoid: [6, 9, 16, 18, 19], // Ardra, Ashlesha, Vishakha, Jyeshtha, Mula
    },
    vehicle_purchase: {
      excellent: [4, 8, 13, 23], // Rohini, Pushya, Hasta, Shravana
      good: [7, 12, 21, 27], // Punarvasu, Uttara Phalguni, Uttara Ashadha, Revati
      avoid: [6, 9, 16, 19], // Ardra, Ashlesha, Vishakha, Mula
    },
    surgery: {
      excellent: [1, 4, 7], // Ashwini, Rohini, Punarvasu
      good: [5, 8, 13, 23], // Mrigashira, Pushya, Hasta, Shravana
      avoid: [2, 6, 9, 16, 18, 19], // Bharani, Ardra, Ashlesha, Vishakha, Jyeshtha, Mula
    },
    ivf_planning: {
      excellent: [4, 8, 13, 27], // Rohini, Pushya, Hasta, Revati
      good: [7, 12, 21, 26], // Punarvasu, Uttara Phalguni, Uttara Ashadha, Uttara Bhadrapada
      avoid: [6, 9, 16, 19, 25], // Ardra, Ashlesha, Vishakha, Mula, Purva Bhadrapada
    },
    cesarean: {
      excellent: [1, 4, 8, 23], // Ashwini, Rohini, Pushya, Shravana
      good: [7, 12, 13, 21, 27], // Punarvasu, Uttara Phalguni, Hasta, Uttara Ashadha, Revati
      avoid: [2, 6, 9, 16, 18, 19, 22], // Bharani, Ardra, Ashlesha, Vishakha, Jyeshtha, Mula, Dhanishta
    },
  };

  const config = favorableNakshatras[activity];
  if (config.excellent.includes(nakshatra)) return 1;
  if (config.good.includes(nakshatra)) return 0.5;
  if (config.avoid.includes(nakshatra)) return -1;
  return 0; // Neutral
}

/**
 * Check if tithi is favorable for an activity
 */
export function getTithiScore(tithi: number, activity: ActivityType): number {
  // Favorable tithis by activity type
  const favorableTithis: Record<ActivityType, { excellent: number[]; avoid: number[] }> = {
    business_start: {
      excellent: [2, 3, 5, 7, 10, 11, 13], // Auspicious tithis for starting ventures
      avoid: [4, 6, 8, 9, 14, 15, 30], // Chaturthi, Shashthi, Ashtami, Navami, Chaturdashi, Amavasya
    },
    property_purchase: {
      excellent: [2, 3, 5, 7, 10, 11, 13],
      avoid: [4, 8, 9, 14, 15, 30],
    },
    graha_pravesh: {
      excellent: [2, 3, 5, 7, 10, 11, 13], // Same as property
      avoid: [4, 8, 9, 14, 15, 30],
    },
    vehicle_purchase: {
      excellent: [2, 3, 5, 7, 11, 13],
      avoid: [4, 6, 8, 9, 14, 30],
    },
    surgery: {
      excellent: [2, 3, 5, 7, 10, 11], // Avoid Ashtami strongly
      avoid: [4, 6, 8, 9, 14, 15, 30],
    },
    ivf_planning: {
      excellent: [2, 3, 5, 7, 10, 11, 13], // Shukla paksha preferred
      avoid: [4, 8, 9, 14, 15, 19, 23, 30], // Avoid Krishna paksha tithis
    },
    cesarean: {
      excellent: [2, 3, 5, 7, 10, 11, 13], // Choose birth time carefully
      avoid: [4, 6, 8, 9, 14, 15, 19, 23, 30],
    },
  };

  const config = favorableTithis[activity];
  if (config.excellent.includes(tithi)) return 1;
  if (config.avoid.includes(tithi)) return -1;
  return 0; // Neutral
}

/**
 * Check if day of week is favorable for an activity
 */
export function getDayOfWeekScore(date: Date, activity: ActivityType): number {
  const dayOfWeek = date.getDay(); // 0=Sunday, 1=Monday, etc.

  const favorableDays: Record<ActivityType, { excellent: number[]; avoid: number[] }> = {
    business_start: {
      excellent: [3, 4], // Wednesday (Mercury), Thursday (Jupiter)
      avoid: [0, 6], // Sunday, Saturday
    },
    property_purchase: {
      excellent: [1, 3, 5], // Monday (Moon), Wednesday, Friday (Venus)
      avoid: [2, 6], // Tuesday (Mars), Saturday
    },
    graha_pravesh: {
      excellent: [1, 4, 5], // Monday, Thursday, Friday
      avoid: [2, 6], // Tuesday, Saturday
    },
    vehicle_purchase: {
      excellent: [3, 4, 5], // Wednesday, Thursday, Friday
      avoid: [2, 6], // Tuesday, Saturday
    },
    surgery: {
      excellent: [1, 3], // Monday, Wednesday
      avoid: [2, 0, 6], // Tuesday (Mars - blood), Sunday, Saturday
    },
    ivf_planning: {
      excellent: [1, 4, 5], // Monday (Moon), Thursday (Jupiter), Friday (Venus)
      avoid: [2, 6], // Tuesday, Saturday
    },
    cesarean: {
      excellent: [1, 3, 4, 5], // Most days except malefic
      avoid: [2, 6], // Tuesday (Mars), Saturday (Saturn)
    },
  };

  const config = favorableDays[activity];
  if (config.excellent.includes(dayOfWeek)) return 1;
  if (config.avoid.includes(dayOfWeek)) return -0.5;
  return 0; // Neutral
}

/**
 * Get all muhurta factors for a given date
 */
export function getMuhurtaFactors(date: Date, moonLongitude: number, latitude?: number, longitude?: number): MuhurtaFactors {
  const tithi = getTithi(date);
  const nakshatra = getNakshatraFromLongitude(moonLongitude);
  const moonPhase = getMoonPhase(date);
  const isWaxingMoon = tithi.paksha === 'shukla';
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const dayOfWeek = dayNames[date.getDay()];
  const hora = getHora(date);
  const rahuKaalActive = isRahuKaal(date, latitude, longitude);
  const gandanta = isGandanta(moonLongitude);

  return {
    tithi,
    nakshatra: nakshatra.number,
    nakshatraName: nakshatra.name,
    isWaxingMoon,
    moonPhase,
    dayOfWeek,
    hora,
    isRahuKaal: rahuKaalActive,
    isGandanta: gandanta,
  };
}

/**
 * Calculate overall muhurta score (0-100) for an activity at a specific time
 */
export function calculateMuhurtaScore(
  date: Date,
  moonLongitude: number,
  activity: ActivityType
): { score: number; factors: MuhurtaFactors; breakdown: Record<string, number> } {
  const factors = getMuhurtaFactors(date, moonLongitude);

  // Calculate individual scores
  const nakshatraScore = getNakshatraScore(factors.nakshatra, activity);
  const tithiScore = getTithiScore(factors.tithi.number, activity);
  const dayScore = getDayOfWeekScore(date, activity);
  const moonPhaseScore = factors.isWaxingMoon ? 0.5 : -0.3; // Prefer waxing moon

  // Weighted average (converted to 0-100 scale)
  const weights = {
    nakshatra: 0.35,
    tithi: 0.30,
    day: 0.20,
    moonPhase: 0.15,
  };

  const rawScore =
    nakshatraScore * weights.nakshatra +
    tithiScore * weights.tithi +
    dayScore * weights.day +
    moonPhaseScore * weights.moonPhase;

  // Convert from -1 to +1 scale to 0-100 scale
  const score = Math.round(((rawScore + 1) / 2) * 100);

  return {
    score: Math.max(0, Math.min(100, score)),
    factors,
    breakdown: {
      nakshatra: Math.round((nakshatraScore + 1) * 50),
      tithi: Math.round((tithiScore + 1) * 50),
      day: Math.round((dayScore + 1) * 50),
      moonPhase: Math.round((moonPhaseScore + 1) * 50),
    },
  };
}

// ==================== PHASE 2: ADVANCED MUHURTA FEATURES ====================

/**
 * Gandanta zones - junctions between water and fire signs (highly inauspicious)
 * These are critical degrees where nakshatra and sign change simultaneously
 */
const GANDANTA_ZONES = [
  { start: 356.4, end: 360, sign: 'Pisces/Aries', nakshatra: 'Revati/Ashwini' }, // 26°40' Pisces to 0° Aries
  { start: 0, end: 3.6, sign: 'Pisces/Aries', nakshatra: 'Revati/Ashwini' }, // 0° to 3°36' Aries
  { start: 116.4, end: 123.6, sign: 'Cancer/Leo', nakshatra: 'Ashlesha/Magha' }, // 26°40' Cancer to 3°36' Leo
  { start: 236.4, end: 243.6, sign: 'Scorpio/Sagittarius', nakshatra: 'Jyeshtha/Mula' }, // 26°40' Scorpio to 3°36' Sagittarius
];

/**
 * Check if Moon is in Gandanta (critical junction point)
 * CRITICAL for childbirth - absolutely avoid
 */
export function isGandanta(moonLongitude: number): boolean {
  for (const zone of GANDANTA_ZONES) {
    if (zone.start > zone.end) {
      // Wraps around 360° (Pisces/Aries)
      if (moonLongitude >= zone.start || moonLongitude <= zone.end) {
        return true;
      }
    } else {
      if (moonLongitude >= zone.start && moonLongitude <= zone.end) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Calculate Rahu Kaal (inauspicious time period) based on day of week
 * Duration: 1/8th of daylight hours (approx 90 minutes)
 * Avoid starting any auspicious activity during this time
 */
export function getRahuKaal(date: Date, latitude: number, longitude: number): {
  start: Date;
  end: Date;
  isActive: boolean;
} {
  const dayOfWeek = date.getDay(); // 0=Sunday, 1=Monday, etc.
  
  // Approximate sunrise/sunset (6 AM to 6 PM = 12 hours)
  // In real implementation, calculate actual sunrise/sunset for location
  const sunrise = new Date(date);
  sunrise.setHours(6, 0, 0, 0);
  const sunset = new Date(date);
  sunset.setHours(18, 0, 0, 0);
  
  const daylightMs = sunset.getTime() - sunrise.getTime();
  const segmentMs = daylightMs / 8; // Each segment = 1.5 hours
  
  // Rahu Kaal timing by day (as fraction of day, starting from sunrise)
  const rahuKaalSegments: Record<number, number> = {
    0: 7, // Sunday: 4:30 PM - 6:00 PM (7th segment)
    1: 1, // Monday: 7:30 AM - 9:00 AM (1st segment)
    2: 6, // Tuesday: 3:00 PM - 4:30 PM (6th segment)
    3: 5, // Wednesday: 12:00 PM - 1:30 PM (5th segment)
    4: 4, // Thursday: 10:30 AM - 12:00 PM (4th segment)
    5: 3, // Friday: 9:00 AM - 10:30 AM (3rd segment)
    6: 2, // Saturday: 9:00 AM - 10:30 AM (2nd segment)
  };
  
  const segment = rahuKaalSegments[dayOfWeek];
  const startTime = sunrise.getTime() + (segment * segmentMs);
  const endTime = startTime + segmentMs;
  
  const start = new Date(startTime);
  const end = new Date(endTime);
  const isActive = date >= start && date <= end;
  
  return { start, end, isActive };
}

/**
 * Check if time falls during Rahu Kaal
 */
export function isRahuKaal(date: Date, latitude: number = 28.6, longitude: number = 77.2): boolean {
  const { isActive } = getRahuKaal(date, latitude, longitude);
  return isActive;
}

/**
 * Calculate natal chart compatibility score for activity (0-100)
 * Checks relevant house strengths for the activity
 */
export function calculateNatalScore(input: NatalChartScoreInput): {
  score: number;
  reasons: string[];
} {
  const { activity, houseStrengths, planetaryPeriods } = input;
  
  if (!houseStrengths) {
    return { score: 50, reasons: ['Natal chart not available - neutral score'] };
  }
  
  const reasons: string[] = [];
  let totalScore = 0;
  let count = 0;
  
  // Relevant houses by activity
  const relevantHouses: Record<ActivityType, number[]> = {
    business_start: [10, 2, 11], // Career, wealth, gains
    property_purchase: [4, 11], // Property, gains
    graha_pravesh: [4], // Property
    vehicle_purchase: [4], // Vehicles
    surgery: [6, 8, 1], // Disease, surgery, body
    ivf_planning: [5, 8], // Children, transformation
    cesarean: [5, 1], // Children, body/ascendant
  };
  
  const houses = relevantHouses[activity] || [];
  
  houses.forEach((house) => {
    const strength = houseStrengths[house];
    if (strength !== undefined) {
      totalScore += strength;
      count++;
      
      if (strength >= 70) {
        reasons.push(`House ${house} very strong (${Math.round(strength)}%)`);
      } else if (strength >= 50) {
        reasons.push(`House ${house} moderate strength (${Math.round(strength)}%)`);
      } else if (strength >= 50) {
        reasons.push(`House ${house} moderate strength (${Math.round(strength)}%)`);
      } else if (strength <= 30) {
        reasons.push(`House ${house} weak (${Math.round(strength)}%)`);
      }
    }
  });
  
  const score = count > 0 ? Math.round(totalScore / count) : 50;
  
  // Check dasha compatibility (simplified)
  if (planetaryPeriods) {
    const { MD, AD, PD, SD } = planetaryPeriods;
    
    // Favorable planets for activities
    const favorablePlanets: Record<ActivityType, string[]> = {
      business_start: ['Jupiter', 'Mercury', 'Sun'],
      property_purchase: ['Venus', 'Mars', 'Moon'],
      graha_pravesh: ['Venus', 'Moon', 'Jupiter'],
      vehicle_purchase: ['Venus', 'Mercury'],
      surgery: ['Mars', 'Sun'], // Mars for courage, Sun for vitality
      ivf_planning: ['Jupiter', 'Venus', 'Moon'],
      cesarean: ['Jupiter', 'Venus', 'Moon'], // Choose strong dasha for child
    };
    
    const favorable = favorablePlanets[activity] || [];
    const runningPlanets = [MD, AD, PD, SD].filter(Boolean);
    
    const favorableCount = runningPlanets.filter((p) => favorable.includes(p)).length;
    if (favorableCount >= 2) {
      reasons.push(`Favorable dasha period (${runningPlanets.slice(0, 2).join('/')})`);
    }
  }
  
  return { score: Math.max(0, Math.min(100, score)), reasons };
}

/**
 * Calculate transit score for activity (0-100)
 * Checks if transiting planets are favorable for the activity
 */
export function calculateTransitScore(input: TransitScoreInput): {
  score: number;
  reasons: string[];
} {
  const { activity, transitPlanets, natalPlanets } = input;
  
  if (!transitPlanets || !natalPlanets) {
    return { score: 50, reasons: ['Transit data not available - neutral score'] };
  }
  
  const reasons: string[] = [];
  let score = 50; // Base score
  
  // Check key transits for each activity
  const jupiter = transitPlanets['Jupiter'];
  const saturn = transitPlanets['Saturn'];
  const mars = transitPlanets['Mars'];
  const venus = transitPlanets['Venus'];
  const mercury = transitPlanets['Mercury'];
  
  switch (activity) {
    case 'business_start':
      // Jupiter transiting 10th/11th/2nd is excellent
      if (jupiter && [10, 11, 2].includes(jupiter.house)) {
        score += 20;
        reasons.push(`Jupiter transiting house ${jupiter.house} (favorable)`);
      }
      // Saturn afflicting 10th is bad
      if (saturn && saturn.house === 10) {
        score -= 15;
        reasons.push('Saturn in 10th house (delays expected)');
      }
      // Mercury strong is good
      if (mercury && [1, 10, 11].includes(mercury.house)) {
        score += 10;
        reasons.push(`Mercury in house ${mercury.house} (communication strength)`);
      }
      break;
      
    case 'property_purchase':
    case 'graha_pravesh':
      // Jupiter blessing H4/H11
      if (jupiter && [4, 11].includes(jupiter.house)) {
        score += 20;
        reasons.push(`Jupiter in house ${jupiter.house} (divine blessing for property)`);
      } else if (jupiter) {
        // Jupiter aspects 5th, 7th, 9th houses from its position
        const jupiterAspects = [
          (jupiter.house + 4) % 12 || 12,
          (jupiter.house + 6) % 12 || 12,
          (jupiter.house + 8) % 12 || 12
        ];
        if (jupiterAspects.includes(4)) {
          score += 15;
          reasons.push(`Jupiter aspects house 4 (property protection)`);
        }
        if (jupiterAspects.includes(11)) {
          score += 10;
          reasons.push(`Jupiter aspects house 11 (gains assured)`);
        }
      }
      
      // Venus/Mars in 4th or aspecting 4th
      if (venus && venus.house === 4) {
        score += 15;
        reasons.push('Venus in 4th house (comfort & luxury)');
      }
      if (mars && [1, 4, 11].includes(mars.house)) {
        score += 10;
        reasons.push(`Mars in house ${mars.house} (courage for property acquisition)`);
      } else if (mars) {
        // Mars aspects 4th, 7th, 8th houses
        const marsAspects = [
          (mars.house + 3) % 12 || 12,
          (mars.house + 6) % 12 || 12,
          (mars.house + 7) % 12 || 12
        ];
        if (marsAspects.includes(4)) {
          score -= 5;
          reasons.push('Mars aspects house 4 (minor conflict in property)');
        }
      }
      
      // Saturn transiting H4 is bad
      if (saturn && saturn.house === 4) {
        score -= 15;
        reasons.push('Saturn in 4th house (delays/obstacles in property)');
      } else if (saturn) {
        // Saturn aspects 3rd, 7th, 10th houses
        const saturnAspects = [
          (saturn.house + 2) % 12 || 12,
          (saturn.house + 6) % 12 || 12,
          (saturn.house + 9) % 12 || 12
        ];
        if (saturnAspects.includes(4)) {
          score -= 10;
          reasons.push('Saturn aspects house 4 (slow progress expected)');
        }
        if (!saturnAspects.includes(4) && saturn.house !== 4) {
          score += 5;
          reasons.push('Saturn NOT afflicting house 4 (obstacles cleared)');
        }
      }
      break;
      
    case 'surgery':
      // Mars should NOT afflict 6th/8th heavily
      if (mars && [6, 8].includes(mars.house)) {
        score -= 15;
        reasons.push(`Mars in house ${mars.house} (caution: bleeding risk)`);
      }
      // Jupiter in 6th/8th can protect
      if (jupiter && [6, 8].includes(jupiter.house)) {
        score += 10;
        reasons.push(`Jupiter in house ${jupiter.house} (protection)`);
      }
      break;
      
    case 'ivf_planning':
    case 'cesarean':
      // Jupiter aspecting/in 5th is excellent
      if (jupiter && [5, 9, 11].includes(jupiter.house)) {
        score += 20;
        reasons.push(`Jupiter in house ${jupiter.house} (children)`);
      }
      // Venus strong for fertility
      if (venus && [1, 5, 7].includes(venus.house)) {
        score += 10;
        reasons.push(`Venus in house ${venus.house} (fertility)`);
      }
      // Saturn in 5th is not ideal
      if (saturn && saturn.house === 5) {
        score -= 10;
        reasons.push('Saturn in 5th (delays in children matters)');
      }
      break;
      
    case 'vehicle_purchase':
      if (venus && [1, 4, 11].includes(venus.house)) {
        score += 15;
        reasons.push(`Venus in house ${venus.house} (luxury items)`);
      }
      if (mercury && [1, 4, 10].includes(mercury.house)) {
        score += 10;
        reasons.push(`Mercury in house ${mercury.house} (vehicles)`);
      }
      break;
  }
  
  return { score: Math.max(0, Math.min(100, score)), reasons };
}

/**
 * Enhanced muhurta score calculation with natal chart + transit integration
 */
export function calculateEnhancedMuhurtaScore(
  date: Date,
  moonLongitude: number,
  activity: ActivityType,
  natalInput?: NatalChartScoreInput,
  transitInput?: TransitScoreInput,
  latitude?: number,
  longitude?: number
): {
  score: number;
  factors: MuhurtaFactors;
  breakdown: Record<string, number>;
  natalScore?: number;
  transitScore?: number;
  blockers: string[];
  recommendations: string[];
} {
  // Get basic muhurta score
  const basicResult = calculateMuhurtaScore(date, moonLongitude, activity);
  
  const blockers: string[] = [];
  const recommendations: string[] = [];
  
  // Check Rahu Kaal - HARD BLOCKER
  const rahuKaalActive = isRahuKaal(date, latitude, longitude);
  if (rahuKaalActive) {
    blockers.push('❌ RAHU KAAL - Avoid starting any auspicious activity');
  }
  
  // Check Gandanta - CRITICAL for childbirth
  const gandanta = isGandanta(moonLongitude);
  if (gandanta) {
    if (activity === 'cesarean') {
      blockers.push('❌ GANDANTA - ABSOLUTELY AVOID for childbirth (extreme danger)');
    } else {
      blockers.push('⚠️ Gandanta zone - highly inauspicious');
    }
  }
  
  // Update factors with Phase 2 data
  const enhancedFactors: MuhurtaFactors = {
    ...basicResult.factors,
    isRahuKaal: rahuKaalActive,
    isGandanta: gandanta,
  };
  
  // Calculate natal and transit scores
  let natalScore: number | undefined;
  let transitScore: number | undefined;
  
  if (natalInput) {
    const natalResult = calculateNatalScore(natalInput);
    natalScore = natalResult.score;
    recommendations.push(...natalResult.reasons);
  }
  
  if (transitInput) {
    const transitResult = calculateTransitScore(transitInput);
    transitScore = transitResult.score;
    recommendations.push(...transitResult.reasons);
  }
  
  // Hard block if Rahu Kaal or (Gandanta + cesarean)
  if (rahuKaalActive || (gandanta && activity === 'cesarean')) {
    return {
      score: 0,
      factors: enhancedFactors,
      breakdown: { ...basicResult.breakdown, rahuKaal: 0, gandanta: gandanta ? 0 : 50 },
      natalScore,
      transitScore,
      blockers,
      recommendations,
    };
  }
  
  // Weighted combination: Muhurta (40%) + Natal (30%) + Transit (30%)
  let finalScore = basicResult.score * 0.4;
  
  if (natalScore !== undefined) {
    finalScore += natalScore * 0.3;
  } else {
    finalScore += 50 * 0.3; // Neutral if not available
  }
  
  if (transitScore !== undefined) {
    finalScore += transitScore * 0.3;
  } else {
    finalScore += 50 * 0.3; // Neutral if not available
  }
  
  // Penalty for Gandanta (other activities)
  if (gandanta && activity !== 'cesarean') {
    finalScore *= 0.7;
    blockers.push('Gandanta penalty applied (-30%)');
  }
  
  return {
    score: Math.round(Math.max(0, Math.min(100, finalScore))),
    factors: enhancedFactors,
    breakdown: {
      ...basicResult.breakdown,
      natal: natalScore || 50,
      transit: transitScore || 50,
    },
    natalScore,
    transitScore,
    blockers,
    recommendations,
  };
}

